import { morph } from '@theme/morph';

const pendingRequests = new Map();
const sectionControllers = new Map();

export function parseHTML(html) {
  return new DOMParser().parseFromString(html, 'text/html');
}

function combinedSignal(...signals) {
  const activeSignals = signals.filter(Boolean);
  if (typeof AbortSignal.any === 'function') return AbortSignal.any(activeSignals);
  const controller = new AbortController();
  for (const signal of activeSignals) {
    if (signal.aborted) controller.abort(signal.reason);
    else signal.addEventListener('abort', () => controller.abort(signal.reason), { once: true });
  }
  return controller.signal;
}

export async function fetchSection(url, sectionId, signal) {
  const requestUrl = new URL(url, window.location.origin);
  requestUrl.searchParams.set('section_id', sectionId);
  requestUrl.searchParams.sort();
  const key = requestUrl.toString();
  const shared = pendingRequests.get(key);
  if (shared) return shared;

  sectionControllers.get(sectionId)?.abort();
  const controller = new AbortController();
  sectionControllers.set(sectionId, controller);
  const request = fetch(requestUrl, {
    headers: { Accept: 'text/html' },
    signal: combinedSignal(controller.signal, signal),
  }).then(async (response) => {
    if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
    return parseHTML(await response.text());
  }).finally(() => {
    if (pendingRequests.get(key) === request) pendingRequests.delete(key);
    if (sectionControllers.get(sectionId) === controller) sectionControllers.delete(sectionId);
  });

  pendingRequests.set(key, request);
  return request;
}

function injectStylesheets(documentResponse) {
  for (const stylesheet of documentResponse.querySelectorAll('link[rel="stylesheet"][href]')) {
    const href = stylesheet.href;
    if (!href || document.querySelector(`link[rel="stylesheet"][href="${CSS.escape(href)}"]`)) continue;
    document.head.append(stylesheet.cloneNode(true));
  }
  for (const stylesheet of documentResponse.querySelectorAll('style[data-section-stylesheet]')) {
    const key = stylesheet.getAttribute('data-section-stylesheet') || stylesheet.id;
    const existing = key
      ? document.querySelector(`style[data-section-stylesheet="${CSS.escape(key)}"], style#${CSS.escape(key)}`)
      : [...document.querySelectorAll('style[data-section-stylesheet]')].find((style) => style.textContent === stylesheet.textContent);
    if (existing) existing.textContent = stylesheet.textContent;
    else document.head.append(stylesheet.cloneNode(true));
  }
}

export function replaceSection(sectionId, html, { mode = 'full' } = {}) {
  const documentResponse = typeof html === 'string' ? parseHTML(html) : html;
  const current = document.getElementById(`shopify-section-${sectionId}`);
  const next = documentResponse.getElementById(`shopify-section-${sectionId}`);
  if (!current || !next) return null;

  injectStylesheets(documentResponse);
  morph(current, next, { mode });
  requestAnimationFrame(() => window.Shopify?.PaymentButton?.init?.());
  return current;
}

export function replaceElements(currentRoot, nextRoot, selectors) {
  for (const selector of selectors) {
    const current = currentRoot.querySelector(selector);
    const next = nextRoot.querySelector(selector);
    if (!current || !next) continue;
    morph(current, next);
  }

  requestAnimationFrame(() => window.Shopify?.PaymentButton?.init?.());
}

export function updateCartCount(count) {
  for (const badge of document.querySelectorAll('[data-cart-count]')) {
    badge.textContent = String(count);
    badge.hidden = count === 0;
  }
}
